import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, GlassCard, GradButton } from "@/components/erp";
import { IsEmriDialog } from "@/components/IsEmriDialog";
import { IsEmriDetay } from "@/components/IsEmriDetay";
import { AraCubugu, Th, BosDurum, trAra, bugun } from "@/components/ortak/Liste";
import { excelAktar, useSayfa } from "@/components/ortak/KayitAraclari";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import api from "@/lib/api";

export const Ilerleme = ({ ie }) => (
  <div className="flex gap-1 mt-2">
    {ie.rota.map((a, i) => <div key={i} title={a.ad} className={`h-1.5 flex-1 rounded-full ${a.durum === "tamam" ? "grad-bg" : a.durum === "aktif" ? "bg-[var(--g1)]/60 animate-pulse" : a.durum === "kismi" ? "bg-amber-400/60" : "bg-white/10"}`} />)}
  </div>
);

const Sekme = ({ secenekler, deger, onChange, testid, kucuk }) => (
  <div className={`flex gap-1 ${kucuk ? "mb-3" : "mb-5 border-b border-white/10"}`}>{secenekler.map(([k, l]) => (
    <button key={k} data-testid={`${testid}-${k}`} onClick={() => onChange(k)}
      className={kucuk ? `px-3 py-1.5 rounded-lg text-xs ${deger === k ? "bg-[var(--g1)] text-white font-semibold" : "bg-white/5 text-muted-foreground"}` : `px-3 py-2.5 text-sm -mb-px border-b-2 ${deger === k ? "border-[var(--g1)] text-[var(--g1)] font-semibold" : "border-transparent text-muted-foreground"}`}>{l}</button>))}</div>
);
export { Sekme };

const ALAN = { ad: "Ürün", kod: "SKU" };
const Stoklar = ({ planlar, onUret }) => {
  const [tip, setTip] = useState("mamul");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [alan, setAlan] = useState("ad");
  const [eksik, setEksik] = useState(false);
  useEffect(() => { api.get("/uretim/planlama", { params: { tip } }).then((r) => setList(r.data)); }, [tip]);
  const rows = useMemo(() => list.filter((u) => (!eksik || u.eksik < 0) && trAra(u[alan]).includes(trAra(q))), [list, q, alan, eksik]);
  const { gorunen, Sayfalama } = useSayfa(rows, `${q}${alan}${tip}${eksik}`);
  const aktar = () => excelAktar(`Planlama_${tip}`, [["Ürün", "ad"], ["SKU", "kod"], ["Kategori", "kategori"], ["Mevcut", "mevcut"], ["Beklenen", "beklenen"], ["Rezerv", "rezerv"], ["Uyarı Seviyesi", "uyari"], ["Eksik Miktar", "eksik"]], rows);
  return (
    <>
      <Sekme kucuk testid="planlama-tip" deger={tip} onChange={setTip} secenekler={[["mamul", "Ürün"], ["yari_mamul", "Yarı Mamul"], ["hammadde", "Hammadde"]]} />
      <GlassCard className="p-4">
        <AraCubugu testid="planlama" alanlar={ALAN} alan={alan} setAlan={setAlan} q={q} setQ={setQ} onExcel={aktar}
          sol={<label className="inline-flex items-center gap-2 text-xs px-3 h-10 rounded-lg border border-white/15"><Checkbox data-testid="planlama-eksik" checked={eksik} onCheckedChange={(v) => setEksik(!!v)} />Yalnızca eksik olanlar</label>} />
        {rows.length === 0 ? <BosDurum text="Kayıt yok." testid="planlama-bos" /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[1000px]" data-testid="planlama-tablo">
            <thead><tr><Th>Ürün</Th><Th>SKU</Th><Th>Kategori</Th><Th right>Mevcut</Th><Th right>Beklenen</Th><Th right>Rezerv</Th><Th right>Uyarı Seviyesi</Th><Th right>Eksik Miktar</Th><Th /></tr></thead>
            <tbody>{gorunen.map((u) => (
              <tr key={u.id} className="border-t border-white/5" data-testid={`planlama-satir-${u.kod}`}>
                <td className="p-2.5 text-xs font-semibold max-w-[320px] truncate" title={u.ad}>{u.ad}</td><td className="p-2.5 font-mono text-xs">{u.kod}</td><td className="p-2.5 text-xs text-muted-foreground max-w-[200px] truncate">{u.kategori || "-"}</td>
                {["mevcut", "beklenen", "rezerv", "uyari"].map((k) => <td key={k} className="p-2.5 text-right font-mono text-xs">{u[k]} {u.birim}</td>)}
                <td className={`p-2.5 text-right font-mono text-xs font-semibold ${u.eksik < 0 ? "text-red-400" : "text-emerald-300"}`} data-testid={`planlama-eksik-${u.kod}`}>{u.eksik} {u.birim}</td>
                <td className="p-2.5 text-right">{planlar && tip !== "hammadde" && <button data-testid={`planlama-uret-${u.kod}`} onClick={() => onUret({ urun_id: u.id, miktar: u.eksik < 0 ? -u.eksik : 1 })} className="inline-flex items-center gap-1 px-3 h-8 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white"><Plus className="w-3.5 h-3.5" />Üret</button>}</td>
              </tr>))}</tbody>
          </table></div>)}
        {rows.length > 0 && <Sayfalama />}
      </GlassCard>
    </>
  );
};

const Ihtiyaclar = ({ list, planlar, onOlustur }) => {
  const [secili, setSecili] = useState([]);
  const toggle = (x) => setSecili((s) => (s.some((y) => y.id === x.id) ? s.filter((y) => y.id !== x.id) : s.length && s[0].urun_id !== x.urun_id ? [x] : [...s, x]));
  return (
    <GlassCard className="p-4">
      {planlar && <div className="flex justify-end mb-3"><GradButton data-testid="ie-olustur-btn" disabled={!secili.length} onClick={() => onOlustur(secili)}><Plus className="w-4 h-4" />İş emri oluştur ({secili.length})</GradButton></div>}
      {list.length === 0 ? <BosDurum text="Planlanacak ihtiyaç yok." testid="ihtiyac-bos" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="ihtiyac-tablo">
          <thead><tr>{planlar && <Th className="w-10" />}<Th>Termin</Th><Th>Sipariş</Th><Th>Müşteri</Th><Th>Ürün</Th><Th right>Miktar</Th></tr></thead>
          <tbody>{list.map((x) => (
            <tr key={x.id} className="border-t border-white/5" data-testid={`ihtiyac-${x.siparis_no}-${x.urun_kod}`}>
              {planlar && <td className="p-2.5"><Checkbox data-testid={`ihtiyac-sec-${x.siparis_no}-${x.kalem_idx ?? 0}`} checked={secili.some((y) => y.id === x.id)} onCheckedChange={() => toggle(x)} /></td>}
              <td className={`p-2.5 font-mono text-xs text-center ${x.termin_tarihi && x.termin_tarihi < bugun() ? "text-red-300" : ""}`}>{x.termin_tarihi}</td>
              <td className="p-2.5 font-mono text-xs">{x.siparis_no}{x.kalem_idx != null ? ` · ${x.kalem_idx + 1}. satır` : ""}</td><td className="p-2.5 text-xs">{x.cari_unvan}</td>
              <td className="p-2.5 text-xs"><span className="font-mono text-muted-foreground mr-2">{x.urun_kod}</span>{x.urun_ad}</td>
              <td className="p-2.5 text-right font-mono font-semibold text-violet-300">{x.miktar} <span className="text-xs text-muted-foreground">{x.birim}</span></td>
            </tr>))}</tbody>
        </table></div>)}
      {planlar && list.length > 0 && <p className="text-xs text-muted-foreground mt-2">Aynı ürünün birden fazla ihtiyacını seçerek tek iş emrinde birleştirebilirsiniz; satırlar ayrı ayrı takip edilir.</p>}
    </GlassCard>
  );
};

export const useUretimRef = (aktif) => {
  const [ref, setRef] = useState(null);
  useEffect(() => {
    if (aktif && !ref) Promise.all([api.get("/urunler"), api.get("/istasyonlar"), api.get("/depolar"), api.get("/uretim/personel")])
      .then(([u, s, d, p]) => setRef({ urunler: u.data, istasyonlar: s.data, depolar: d.data, personel: p.data }));
  }, [aktif, ref]);
  return ref;
};

export default function UretimPlani() {
  const { user } = useAuth();
  const [sekme, setSekme] = useState("stok");
  const [ihtiyac, setIhtiyac] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [detay, setDetay] = useState(null);
  const planlar = can(user, "uretim:plan");
  const ref = useUretimRef(!!dlg);
  const load = useCallback(async () => setIhtiyac((await api.get("/uretim-ihtiyaclari")).data), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="uretim-page">
      <PageHeader title="Planlama" desc="Stok durumuna göre eksik ürünleri görün ve 'Üret' ile iş emri açın; siparişlerden gelen üretim ihtiyaçları satır satır listelenir." />
      <Sekme testid="planlama-sekme" deger={sekme} onChange={setSekme} secenekler={[["stok", "Stoklar"], ["ihtiyac", `Üretim İhtiyaçları (${ihtiyac.length})`]]} />
      {sekme === "stok" ? <Stoklar planlar={planlar} onUret={(o) => setDlg({ onerilen: o, ihtiyaclar: [] })} /> : <Ihtiyaclar list={ihtiyac} planlar={planlar} onOlustur={(s) => setDlg({ ihtiyaclar: s })} />}
      {dlg && ref && <IsEmriDialog ihtiyaclar={dlg.ihtiyaclar} onerilen={dlg.onerilen} {...ref} onClose={() => setDlg(null)} onSaved={(d) => { setDlg(null); load(); setDetay(d.id); }} />}
      {detay && <IsEmriDetay id={detay} onClose={() => setDetay(null)} onChanged={load} />}
    </div>
  );
}
